/**
 * Перенос `lib/src/utils/sse/sse_failure.dart`.
 *
 * Dart `sealed class` перенесён как размеченное объединение по полю `kind`:
 * имена вариантов совпадают с именами Dart-классов, а `switch (failure.kind)`
 * проверяется компилятором на полноту так же, как `switch` по sealed-классу.
 *
 * `Duration` переносится числом миллисекунд.
 */

/**
 * Причина, по которой SSE-поток не состоялся или оборвался.
 *
 * Пакет flutter_http_sse отдавал в onError только строку, поэтому отличить
 * «сервер лёг» от «сервер не принял запрос» было нечем, а 4xx он вообще
 * не считал ошибкой. Здесь причина типизирована.
 */
interface SseFailureBase {
  /** Код ответа, если сервер успел ответить. */
  readonly statusCode: number | null;

  /** Тело ответа для не-2xx — в нём обычно объяснение от бекенда. */
  readonly body: string | null;

  /** Исходное исключение транспорта, если оно было. */
  readonly cause: unknown;
}

/** До сервера не дошли: DNS, отказ в соединении, оборванная сеть. */
export interface SseUnreachable extends SseFailureBase {
  readonly kind: 'unreachable';
}

/** Сервер не ответил за отведённое время на установку соединения. */
export interface SseConnectTimeout extends SseFailureBase {
  readonly kind: 'connectTimeout';
}

/**
 * Соединение открыто, но событий нет дольше допустимого простоя.
 *
 * Именно этот случай раньше вешал экран навсегда: не приходило ни onDone,
 * ни onError, и приложение считало, что ответ всё ещё идёт.
 */
export interface SseSilent extends SseFailureBase {
  readonly kind: 'silent';
  /** Допустимый простой в миллисекундах. */
  readonly idleTimeout: number;
}

/**
 * Сервер не принял запрос: 4xx. Раньше тело такого ответа читалось как
 * поток событий, ничего не парсилось и вызывался onDone — «успешно».
 */
export interface SseBadRequest extends SseFailureBase {
  readonly kind: 'badRequest';
  readonly statusCode: number;
}

/** Сервер упал: 5xx. */
export interface SseServerDown extends SseFailureBase {
  readonly kind: 'serverDown';
  readonly statusCode: number;
}

/** Поток оборвался посреди передачи. */
export interface SseBroken extends SseFailureBase {
  readonly kind: 'broken';
}

export type SseFailure =
  | SseUnreachable
  | SseConnectTimeout
  | SseSilent
  | SseBadRequest
  | SseServerDown
  | SseBroken;

export type SseFailureKind = SseFailure['kind'];

/** Поля базового Dart-класса, которых у варианта нет, всегда `null`. */
const NO_RESPONSE: Pick<SseFailureBase, 'statusCode' | 'body'> = {
  statusCode: null,
  body: null,
};

export function sseUnreachable(cause: unknown = null): SseUnreachable {
  return { kind: 'unreachable', ...NO_RESPONSE, cause };
}

export function sseConnectTimeout(cause: unknown = null): SseConnectTimeout {
  return { kind: 'connectTimeout', ...NO_RESPONSE, cause };
}

export function sseSilent(idleTimeout: number): SseSilent {
  return { kind: 'silent', ...NO_RESPONSE, cause: null, idleTimeout };
}

export function sseBadRequest(statusCode: number, body: string | null = null): SseBadRequest {
  return { kind: 'badRequest', statusCode, body, cause: null };
}

export function sseServerDown(statusCode: number, body: string | null = null): SseServerDown {
  return { kind: 'serverDown', statusCode, body, cause: null };
}

export function sseBroken(cause: unknown = null): SseBroken {
  return { kind: 'broken', ...NO_RESPONSE, cause };
}

/**
 * Имя Dart-класса для каждого варианта: `toString()` в Dart начинался
 * с `runtimeType`, и логи сравниваются именно с этими строками.
 */
const DART_CLASS_NAME: Record<SseFailureKind, string> = {
  unreachable: 'SseUnreachable',
  connectTimeout: 'SseConnectTimeout',
  silent: 'SseSilent',
  badRequest: 'SseBadRequest',
  serverDown: 'SseServerDown',
  broken: 'SseBroken',
};

/** `SseFailure.toString()`: `SseBadRequest status=404 cause=…`. Тело не пишется. */
export function sseFailureToString(failure: SseFailure): string {
  const parts: string[] = [DART_CLASS_NAME[failure.kind]];
  if (failure.statusCode !== null) parts.push(`status=${failure.statusCode}`);
  if (failure.cause !== null && failure.cause !== undefined) {
    parts.push(`cause=${String(failure.cause)}`);
  }
  return parts.join(' ');
}
